import { QText } from '@qualio/ui-components';
import i18n from 'i18next';
import * as mime from 'mime';
import { integrationsApiClient } from '../api';
import {
  QualioDocument,
  QualioDocumentAttachment,
} from '../api/model/document';
import {
  EXTERNAL_SOURCE_FILE_ERROR,
  logCustomError,
} from '../messages/LogErrorMessages';
import {
  DownloadUrlResponse,
  downloadUrlResponseSchema,
} from '../views/components/FileDocumentEditor/types';
import {
  QFilePickerError,
  RemoteFileMetadata,
} from '../views/components/QFilePicker';

export const getAcceptedFileDocumentExts = () => [
  '.png',
  '.bmp',
  '.jpg',
  '.pdf',
  '.doc',
  '.docx',
  '.odp',
  '.pptx',
  '.rtf',
  '.xlsx',
];

export const BULK_FILE_UPLOAD_PAGE_DESCRIPTION_MESSAGE = (
  <QText fontSize="sm">
    {i18n.t('bulkUpload.importExternalFilesDescription')}
    <br />
    {i18n.t('bulkUpload.filesCreatedAsDrafts')}
  </QText>
);

export const FILE_UPLOAD_SIZE_LIMIT_MB = 100;
export const BULK_FILE_UPLOAD_FILES_LIMIT = 50;

export const getFileAttachmentFromFileUploadDocument = (
  fileDocument: QualioDocument,
) => {
  const section = fileDocument.sections[0];
  if (!section) return undefined;
  let attachmentId: number;
  if (section.content?.length > 0 && isNumber(section.content)) {
    attachmentId = +section.content;
  }
  const fileDocumentAttachment =
    fileDocument.sections[0].section_attachments?.find(
      (attachment) => attachment.id === attachmentId,
    );
  return fileDocumentAttachment;
};

export const getFileDocumentExtension = (
  fileDocument: QualioDocument,
): string | undefined => {
  const fileAttachment = getFileAttachmentFromFileUploadDocument(fileDocument);
  const fileExtension = getFileExtension(fileAttachment?.filename ?? '');
  return fileExtension?.toLocaleLowerCase();
};

export const translateExtensionToMimeType = (ext: string) => {
  return mime.getType(ext) ?? '';
};

export const removeFileExtension = (filename: string) =>
  translateExtensionToMimeType(getFileExtension(filename) ?? '')
    ? filename.split('.').slice(0, -1).join('.')
    : filename; // Re-join with periods in case there are any periods in the filename

export const getFileExtension = (filename: string) => {
  const parts = filename.split('.');
  if (parts.length > 1) {
    return parts[parts.length - 1];
  } else {
    return undefined;
  }
};

export const fileTypeFilter = (attachment: QualioDocumentAttachment) => {
  return (
    attachment.preview_state !== 'unsupported' &&
    checkFileExtensionMatchesAcceptedMimeTypes(attachment.filename)
  );
};

export const checkFileExtensionMatchesAcceptedMimeTypes = (
  filename: string,
) => {
  const fileExtension = getFileExtension(filename);
  if (!fileExtension) {
    return false;
  }
  const mimeType = mime.getType(fileExtension);

  const acceptedFileDocumentExts = getAcceptedFileDocumentExts();
  const allowed_mime_types = acceptedFileDocumentExts.map(
    translateExtensionToMimeType,
  );
  return mimeType && allowed_mime_types.includes(mimeType);
};

export const getFilesFromList = (files: FileList | null) => {
  if (files === null) {
    return files;
  }

  return Array.from(files);
};

export const ExternalFileToastMessages: {
  [name: string]: { title: string; message: string };
} = {
  FETCH_LATEST_FAILED: {
    title: i18n.t('errors.failedToUpdate'),
    message: i18n.t('errors.failedToUpdateFile'),
  },
  FETCH_LATEST_SUCCESS: {
    title: i18n.t('messages.updated'),
    message: i18n.t('messages.documentUpdatedToLatestVersion'),
  },
  ADD_FILE_FAILED: {
    title: i18n.t('notifications.failedToAddFile'),
    message: i18n.t('errors.errorAddingFile'),
  },
  ADD_MULTIPLE_FILES_FAILED: {
    title: i18n.t('notifications.failedToAddFiles'),
    message: i18n.t('errors.errorAddingSomeFiles'),
  },
} as const;

export const isNumber = (text: string): boolean => /^\d+$/.test(text);

export const buildErrorPickHandler = (
  handlePickError: (
    errorProps: (typeof ExternalFileToastMessages)[keyof typeof ExternalFileToastMessages],
    message?: string | undefined,
  ) => void,
  multiple: boolean,
) => {
  return (errors: QFilePickerError[]) => {
    logCustomError(EXTERNAL_SOURCE_FILE_ERROR, { error: errors });
    const errorMessage =
      Array.isArray(errors) && errors.length ? errors[0].message : undefined;
    const errorProps = multiple
      ? ExternalFileToastMessages.ADD_MULTIPLE_FILES_FAILED
      : ExternalFileToastMessages.ADD_FILE_FAILED;
    handlePickError(errorProps, errorMessage);
  };
};

export const getExternalFileDownloadUrlProps = async (
  remoteFileMetadata: RemoteFileMetadata,
): Promise<DownloadUrlResponse> => {
  const response = await integrationsApiClient.get(
    remoteFileMetadata.downloadUrl,
  );

  const parsedDownloadUrlResponse = downloadUrlResponseSchema.parse(
    response.data,
  );
  return parsedDownloadUrlResponse;
};
